import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Rect, Text, Circle, Line, Path, Ellipse } from 'react-konva';
import { adaptImageSizes } from '../canvas/utils.js';
import Konva from 'konva';
import { isNotEmpty } from '../../lib/ep.js';
import { localizeFields } from '../builder/DynamicFields.js';

const punch_item_size = 60;	// px
const player_padding = 10;

const standard_board_size = { w:600, h:600 };	// px
const standard_cta_button_size = 8;
const default_answer_json = { winner:false, matches:[], players:[] };

export const CardPlayerImage = ({ player, stage, image_data, item_sizes, refs, ...props }) => {
	const padding = props?.padding ?? player_padding;

	const has_image = (image_data != undefined) ? true : false;
	const image_scale = (has_image) ? ((item_sizes.player_size - 2 * padding) / Math.min(image_data.image_w, image_data.image_h)) : 1;

	const index = props?.index ?? 0;

	const border_radius = parseInt(stage?.faceoff_card_border_radius ?? 30);
	const border_color_overwrite = stage?.faceoff_card_border_color_overwrite ?? true;
	const border_color = stage?.faceoff_card_border_color ?? 'rgba(255, 255, 255, 1)';
	const border_width = parseInt(stage?.faceoff_card_border_width ?? 3);

	const color = props?.color;

	const player_props = {
		width:item_sizes.player_size - 2 * padding,
		height:item_sizes.player_size - 2 * padding,
		offsetX:item_sizes.player_size / 2,
		offsetY:item_sizes.player_size / 2,
		x:item_sizes.player_size / 2 + padding,
		y:item_sizes.player_y_ini,
		cornerRadius:item_sizes.player_size * border_radius / 100 / 2,
	};

	return (
		<>
			{(item_sizes.shadow_w != undefined) && (
				<Ellipse
					ref={(node) => { refs[`shadow_${index}`] = node; }}
					width={item_sizes.shadow_w}
					height={item_sizes.shadow_h}
					fill='black'
					offsetY={item_sizes.shadow_h / 2}
					x={item_sizes.player_size / 2}
					y={item_sizes.player_size + item_sizes.shadow_h / 2 - padding}
					filters={[Konva.Filters.Blur]}
					blurRadius={30}
					opacity={0.2}
					scaleX={0.8}
					preventDefault={props.prevent_default}
				/>
			)}

			<Rect
				ref={(node) => { refs[`player_${index}`] = node; }}
				{...player_props}
				fill={color}
				fillPatternImage={image_data?.fillPatternImage ?? null}
				fillPatternRepeat='no-repeat'
				fillPatternScaleX={image_scale}
				fillPatternScaleY={image_scale}
				fillPriority={(has_image) ? 'pattern' : 'fill'}
				stroke={(border_color_overwrite) ? border_color : color}
				strokeWidth={border_width / image_scale * (props?.canvas_scale ?? 1)}
				preventDefault={props.prevent_default}
			/>

			<Rect
				ref={(node) => { refs[`player_${index}_back`] = node; }}
				{...player_props}
				fill={color}
				strokeWidth={border_width}
				opacity={0}
				preventDefault={props.prevent_default}
			/>
		</>
	)
}

const PickFavoriteCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const bg_image = props.indexed_images?.bg_image ?? false;

	const players = localizeFields(stage?.faceoff_players ?? [],props.locale);

	const text_color_overwrite = stage?.faceoff_card_text_color_overwrite ?? true;
	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';
	const text_border_width = parseInt(stage?.faceoff_text_border_width ?? '0');
	const display_background_color = stage?.faceoff_display_background_color ?? false;
	const display_stars = stage?.faceoff_display_stars ?? true;
	const display_outter_border = stage?.display_outter_border ?? true;
	const display_player_text = stage?.faceoff_display_player_text ?? true;

	const player_text_padding = 20;
	const player_block_text_h = (display_player_text) ? 50 : 1;	// do not set to 0, or the cache will break

	const refs = useRef([]);
	const size = useRef({ ...standard_board_size });
	const in_transition = useRef(false);
	const is_playable = useRef(false);
	const answers = useRef(default_answer_json);
	const preselected_player = useRef(false);
	const start_time = useRef(false);
	const item_sizes = useRef({});
	const cta_size = useRef(size.current.w / 10);
	const contents_h = useRef(0);
	const zoomed_player_scale = useRef(2);
	const gradient_created = useRef(false);
	const canvas_scale = useRef(size.current.w / standard_board_size.w);
	const player_text_h = useRef(player_block_text_h * canvas_scale.current);
	const padding = useRef(player_padding * canvas_scale.current);
	const start_tap_y = useRef(false);
	const already_mounted = useRef(false);

	const max_items_per_row = (players.length > 12) ? 4 : 3;

	const calculatePlayerSize = (use_default_size = false) => {
		// we force the size as if there are 3 items per row
		const forced_items_per_row = max_items_per_row;

		const canvas_w = (use_default_size === false) ? size.current.w : standard_board_size.w;

		return (canvas_w - padding.current * (forced_items_per_row + 1)) / forced_items_per_row;
	};

	const player_scale = useRef(calculatePlayerSize() / calculatePlayerSize(true));

	const getNumPlayersPerRow = () => {
		if(max_items_per_row == 4) {
			// special cases
			if([18, 21, 27].includes(players.length)) return 3;

			// Otherwise, make it 3 items per row:
			return 4;
		}

		// If the total number of players is even (i.e. divisible by 2) but NOT divisible by 3, make it 2 items per row:
		if(max_items_per_row == 3 && (players.length / 2) == parseInt(players.length / 2) && (players.length / 3) != parseInt(players.length / 3)) return 2;

		// Otherwise, make it 3 items per row:
		return max_items_per_row;
	};

	const num_players_per_row = getNumPlayersPerRow();

	const generateItemSizes = () => {
		const player_size = calculatePlayerSize();
		const shadow_w = player_size * 0.9;

		item_sizes.current = {
			player_size,
			player_text_w:player_size - player_text_padding,
			player_x_ini:0,
			player_y_ini:player_size / 2 + padding.current,
			player_text_y_ini:3 * size.current.h / 4 - 2 * player_text_padding,
			shadow_w,
			shadow_h:player_size / 8,
			shadow_x_ini:(player_size - shadow_w) / 2,
			shadow_y_ini:player_size / 1.8,
		};
	};

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const default_star_size = 80;
	const whole_stars_size = 150;

	const cta_stroke_color = 'rgba(255, 255, 255, 1)';

	const selection_animation_duration = 0.6; // in seconds

	const stars = [{
		size:default_star_size,
		x:-35,
		y:35,
	}, {
		size:60,
		x:45,
		y:-5,
	}, {
		size:50,
		x:-10,
		y:-50,
	}, {
		size:40,
		x:10,
		y:-70,
	}];

	const animateStars = () => {
		if(!display_stars) return;

		stars.map((star, s) => {
			const star_ref = refs.current[`star_${s + 1}`];
			const total_rotation = 20 - 40 * Math.random();

			star_ref.setAttrs({
				scaleX:0,
				scaleY:0,
				y:star.y + 30,
				rotation:0,
			});

			setTimeout(() => {
				if(star_ref == undefined) return;

				star_ref.to({
					scaleX:star.size / default_star_size,
					scaleY:star.size / default_star_size,
					y:star.y - 25,
					rotation:total_rotation * 0.55,
					duration:0.5,
					easing:Konva.Easings.StrongEaseIn,
					onFinish:() => {
						star_ref.to({
							scaleX:0,
							scaleY:0,
							y:star.y - 70,
							rotation:total_rotation,
							duration:0.5,
							easing:Konva.Easings.StrongEaseOut,
						});
					},
				});
			}, (100 + s * 80) * Math.random());
		});
	};

	const gameReset = () => {
		is_playable.current = true;
		answers.current = default_answer_json;
		generateItemSizes();
	};

	const applyAllCaches = (apply_shadows = true) => {
		if(refs.current['bg'] != undefined) refs.current['bg'].cache(default_cache_props);
		if(refs.current['ok_cta'] != undefined) refs.current['ok_cta'].cache(default_cache_props);
		if(refs.current['ko_cta'] != undefined) refs.current['ko_cta'].cache(default_cache_props);

		players.map((foo, p) => {
			if(refs.current[`player_${p}`] == undefined) return;

			if(apply_shadows && refs.current[`shadow_${p}`] != undefined) {
				refs.current[`shadow_${p}`].cache({ ...default_cache_props, offset:30 });
			}

			if(refs.current[`player_${p}_text_all`] != undefined) refs.current[`player_${p}_text_all`].cache(default_cache_props);
			if(refs.current[`player_${p}_selected_all`] != undefined) refs.current[`player_${p}_selected_all`].cache(default_cache_props);
			if(refs.current[`player_${p}`] != undefined) refs.current[`player_${p}`].cache(default_cache_props);
		});
	};

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current.w = size_received;
				canvas_scale.current = size.current.w / standard_board_size.w;
				player_text_h.current = player_block_text_h * canvas_scale.current;
				padding.current = player_padding * canvas_scale.current;

				player_scale.current = calculatePlayerSize() / calculatePlayerSize(true);

				gameReset();

				const num_rows = Math.ceil(players.length / num_players_per_row);
				const all_text_heights = num_rows * player_text_h.current;

				contents_h.current = num_rows * item_sizes.current.player_size + (num_rows + 1) * padding.current + all_text_heights;

				size.current.h = Math.max(size_received, contents_h.current);

				let expected_scale = 2.5;
				if(num_rows < 3) expected_scale = 2;

				zoomed_player_scale.current = expected_scale * (size.current.w / standard_board_size.w);
				cta_size.current = size.current.w / 10;

				setFullLoaded(true);

				setTimeout(() => {
					applyAllCaches();

					mountBoard();
				}, 100);
			});
		} else {
			setFullLoaded(true);
		}
	};

	useEffect(() => {
		if(props.loaded_status != 'LOADED') return;
		gameInit();
	}, [
		props.loaded_status,
		props.indexed_images,
		stage.faceoff_random_display,
		stage.faceoff_players,
	]);

	useEffect(() => {
		applyAllCaches(false);
	}, [
		stage.faceoff_card_border_width,
		stage.faceoff_card_border_color,
		stage.faceoff_card_border_color_overwrite,
		stage.faceoff_card_text_color_overwrite,
		stage.faceoff_card_border_radius,
		stage.faceoff_text_border_width,
		stage.text_color,
		stage.faceoff_players,
	]);
 
	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			cb(success_game);
		},
	}), []);

	const images_adapter = (image) => (adaptImageSizes(image, punch_item_size, 300, 500));

	const indexed_images = useMemo(() => {
		const adapted = {};
		for(var img in props.indexed_images) {
			adapted[img] = images_adapter(props.indexed_images[img]);
		}
		return adapted;
	}, [ props.indexed_images ]);

	const finishGame = () => {
		is_playable.current = false;

		transitionEnd(() => {
			props.onEnded(true, { faceoff:answers.current });
		});
	};

	const getColor = (player_num) => {
		if(isNotEmpty(players[player_num], 'color')) return players[player_num].color;

		const color = indexed_images[`image_${players[player_num].id}`]?.avg_color;
		if(color != undefined) return `rgba(${color.r}, ${color.g}, ${color.b}, 1)`;

		return 'rgba(30, 34, 170, 1)';
	};

	const transitionEnd = (cb) => {
		if(gradient_created.current === false) {
			const gradient = new Konva.Rect({
				fillRadialGradientStartPoint:{ x:size.current.w / 2, y:size.current.h / 2 },
				fillRadialGradientStartRadius:0,
				fillRadialGradientEndPoint:{ x:size.current.w / 2, y:size.current.h / 2 },
				fillRadialGradientEndRadius:360,
				fillRadialGradientColorStops:[0, 'rgba(0, 0, 0, 0)', 0.7, 'rgba(0, 0, 0, 0)', 1, 'rgba(0, 0, 0, 0.5)'],
				width:size.current.w,
				height:size.current.h,
				preventDefault:false,
			});

			refs.current['final_gradient'].add(gradient);

			refs.current['final_gradient'].cache(default_cache_props);

			gradient_created.current = true;
		} else {
			refs.current['final_gradient'].setAttrs({
				x:0,
			});

			refs.current['layer'].draw();
		}

		refs.current['final_gradient'].to({
			opacity:0.6,
			duration:0.5,
		});

		const directions = [];

		let total_discards = 0;
		players.map((player, p) => {
			if(p == preselected_player.current) {
				setTimeout(() => {
					if(refs.current[`player_${preselected_player.current}_all`] == undefined) return;

					refs.current[`player_${preselected_player.current}_all`].to({
						y:size.current.h / 2,
						duration:1,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(cb != undefined) cb;
						},
					});
	
				}, (players.length * 20 + 60));
				return;
			}

			let x_direction;
			let x_direction_delta;
			if(refs.current[`player_${p}_all`].x() < (size.current.w / 2)) {
				// go left
				x_direction = -1;
				x_direction_delta = 1;
			} else if(refs.current[`player_${p}_all`].x() > (size.current.w / 2)) {
				// go right
				x_direction = 1;
				x_direction_delta = -1;
			} else if((p / 2) == parseInt(p / 2)) {
				// centered horizontally, but even index, go right
				x_direction = 1;
				x_direction_delta = -1;
			} else {
				// centered horizontally, but odd index, go left
				x_direction = -1;
				x_direction_delta = 1;
			}

			let y_direction;
			let y_direction_delta;
			if(refs.current[`player_${p}_all`].y() < (size.current.h / 2)) {
				// go up
				y_direction = -1;
				y_direction_delta = 1;
			} else if(refs.current[`player_${p}_all`].y() > (size.current.h / 2)) {
				// go bottom
				y_direction = 1;
				y_direction_delta = -1;
			} else if((p / 2) != parseInt(p / 2)) {
				// centered vertically, but odd index, go bottom
				y_direction = 1;
				y_direction_delta = -1;
			} else {
				// centered vertically, but even index, go top
				y_direction = -1;
				y_direction_delta = 1;
			}

			let final_x;
			if(x_direction < 0) {
				final_x = -0.2 * item_sizes.current.player_size;
			} else {
				final_x = size.current.w + 0.2 * item_sizes.current.player_size;
			}

			let final_y;
			if(y_direction < 0) {
				final_y = -0.2 * item_sizes.current.player_size;
			} else {
				final_y = size.current.h + 0.2 * item_sizes.current.player_size;
			}

			setTimeout(() => {
				if(refs.current[`player_${p}_all`] == undefined) return;

				const random_xy = 0.5 * item_sizes.current.player_size * Math.random();

				if(display_player_text) {
					refs.current[`player_${p}_text_all`].to({
						opacity:0,
						duration:1,
						easing:Konva.Easings.StrongEaseInOut,
					});
				}

				directions.push({
					item:`player_${p}_all`,
					x_direction,
					y_direction,
				});

				refs.current[`player_${p}_all`].to({
					x:final_x -1 * x_direction_delta * random_xy + x_direction_delta * item_sizes.current.player_size / 2,
					y:final_y -1 * y_direction_delta * random_xy + y_direction_delta * item_sizes.current.player_size / 2,
					rotation:360 + 90 * Math.random(),
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						total_discards++;

						if(total_discards == players.length - 1) {
							setTimeout(() => {
								unmountBoard(directions);
							}, 2000);
						}
					},
				});

				refs.current[`shadow_${p}`].to({
					opacity:0,
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
				});
			}, (p * 20));
		});

		if(display_player_text) {
			refs.current[`player_${preselected_player.current}_text_all`].to({
				scaleX:1,
				scaleY:1,
				opacity:1,
				y:item_sizes.current.player_size,
				duration:1.2,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current[`player_${preselected_player.current}_selected_text`].to({
				opacity:0,
				duration:1.2,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`player_${preselected_player.current}_bg_cover`].to({
			opacity:0,
			duration:1.2,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['tapper'].to({
			opacity:0,
			duration:1.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				refs.current['tapper'].setAttrs({
					x:size.current.w + 10,
				});
			},
		});
	};

	const resetGame = () => {
		mountBoard();

		is_playable.current = true;

		if(refs.current['final_gradient'] != undefined) {
			refs.current['final_gradient'].setAttrs({
				x:size.current.w + 10,
			});

			refs.current['layer'].draw();
		}
	};

	const unmountBoard = (directions) => {
		if(refs.current['final_gradient'] != undefined) {
			refs.current['final_gradient'].to({
				opacity:0,
				duration:1,
			});
		}

		directions.map((item, i) => {
			if(refs.current[item.item] == undefined) return;

			let final_x = size.current.w + item_sizes.current.player_size + 10;
			if(item.x_direction < 0) final_x = -1 * (item_sizes.current.player_size + 10);

			let final_y = size.current.h + item_sizes.current.player_size + 10;
			if(item.y_direction < 0) final_y = -1 * (item_sizes.current.player_size + 10);

			refs.current[item.item].to({
				x:final_x,
				y:final_y,
				duration:1,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					if(i < directions.length - 1) return;

					props.onEnded(true, { faceoff:answers.current });

					already_mounted.current = false;

					if(is_builder && !is_test) {
						resetGame();
					}
				},
			});
		});
	};

	const animateSelected = (cb) => {
		const duration = 0.3;

		const ini_props = {
			y:item_sizes.current.player_y_ini - 3 * padding.current,
			rotation:15,
			scaleX:-1.05,
			scaleY:1.2,
			easing:Konva.Easings.StrongEaseIn,
		};

		const end_props = {
			y:item_sizes.current.player_y_ini,
			rotation:0,
			scaleX:1,
			scaleY:1,
			easing:Konva.Easings.StrongEaseOut,
		};

		refs.current[`player_${preselected_player.current}_back`].to({
			...ini_props,
			duration,
			onFinish:() => {
				refs.current[`player_${preselected_player.current}_back`].to({
					...end_props,
					duration,
				});
			},
		});

		setTimeout(() => {
			if(refs.current[`player_${preselected_player.current}_back`] == undefined) return;

			refs.current[`player_${preselected_player.current}_back`].to({
				opacity:1,
				duration:0.01,
				onFinish:() => {
					setTimeout(() => {
						if(refs.current[`player_${preselected_player.current}_back`] == undefined) return;

						refs.current[`player_${preselected_player.current}_back`].to({
							opacity:0,
							duration:0.01,
						});

						if(cb != undefined) cb();
					}, (0.3 * duration * 1000));
				},
			});
		}, (0.85 * duration * 1000));

		refs.current[`player_${preselected_player.current}`].to({
			...ini_props,
			duration,
			onFinish:() => {
				refs.current[`player_${preselected_player.current}`].to({
					...end_props,
					duration,
				});
			},
		});
	};

	const voteForPlayer = (player) => {
		const end_time = Date.now();
		if(answers.current.players[player] == undefined) {
			answers.current.players[player] = {
				votes:0,
				matches:[],
				defeated_by:false,
				//index:player,
				player_id:players[player].id,
			};
		}

		answers.current.players[player].votes++;
		//answers.current.players[player].matches.push(rival);

		answers.current.winner = players[player].id;

		/*if(answers.current.players[rival] == undefined) {
			answers.current.players[rival] = {
				votes:0,
				matches:[],
				defeated_by:false,
				//index:rival,
				player_id:players[rival].id,
			};
		}*/

		/*answers.current.players[rival].matches.push(player);
		answers.current.players[rival].defeated_by = players[player].id;*/

		answers.current.matches.push({
			id:answers.current.matches.length,
			time:(!is_builder || is_test) ? (end_time - start_time.current) / 1000 : 0,
			winner:players[player].id,
			//loser:players[rival].id,
		});
	};

	const enablePickPlayer = () => {
		if(is_builder && !is_test) return;

		start_time.current = Date.now();
	};

	const adaptZIndexes = () => {
		refs.current['tapper'].setAttrs({
			x:0,
			zIndex:players.length,
		});

		let accumulative_index = 1;

		players.map((foo, p) => {
			if(p == preselected_player.current) {
				refs.current[`player_${p}_all`].setAttrs({
					zIndex:players.length + 2,
					preselected:true,
				});
				return;
			}

			refs.current[`player_${p}_all`].setAttrs({
				zIndex:accumulative_index,
			});

			accumulative_index++;
		});

		refs.current['final_gradient'].setAttrs({
			zIndex:players.length + 2,
		});

		refs.current['confirm_actions'].setAttrs({
			zIndex:players.length + 3,
		});

		refs.current['all_stars'].setAttrs({
			zIndex:players.length + 4,
		});

		refs.current['layer'].draw();
	};

	const selectPlayer = (player_num) => {
		if(is_playable.current === false) return;
		if(in_transition.current === true) return;
		if(preselected_player.current !== false) return;

		in_transition.current = true;

		preselected_player.current = player_num;

		setTimeout(() => {
			if(refs.current['tapper'] == undefined) return;

			refs.current['tapper'].setAttrs({
				x:0,
			});

			refs.current['tapper'].to({
				opacity:0.7,
				duration:selection_animation_duration / 2,
			});
		}, ((selection_animation_duration / 2) * 1000));

		adaptZIndexes();

		refs.current[`player_${player_num}_bg_cover`].to({
			opacity:1,
			stroke:cta_stroke_color,
			strokeWidth:padding.current,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
		});

		if(display_player_text) {
			refs.current[`player_${player_num}_text_all`].to({
				scaleX:0.8,
				scaleY:0.8,
				y:item_sizes.current.player_size - 0.5 * padding.current,
				opacity:0,
				duration:selection_animation_duration,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current[`player_${player_num}_selected_text`].to({
				opacity:1,
				duration:selection_animation_duration,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`player_${player_num}_all`].to({
			x:size.current.w / 2,
			y:size.current.h / 2 - cta_size.current + 2 * padding.current,
			scaleX:zoomed_player_scale.current,
			scaleY:zoomed_player_scale.current,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				in_transition.current = false;

				appearCTAs();
			},
		});
	};

	const undoSelection = () => {
		if(is_playable.current === false) return;
		if(in_transition.current === true) return;
		if(preselected_player.current === false) return;

		const foo = preselected_player.current;

		refs.current['tapper'].to({
			opacity:0,
			duration:selection_animation_duration,
			onFinish:() => {
				refs.current['tapper'].setAttrs({
					x:size.current.w + 10,
					zIndex:players.length,
				});
			},
		});

		refs.current['layer'].draw();

		disappearCTAs();

		refs.current[`player_${preselected_player.current}_bg_cover`].to({
			opacity:0,
			stroke:cta_stroke_color,
			strokeWidth:0,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
		});

		setTimeout(() => {
			if(refs.current[`player_${foo}_all`] == undefined) return;

			refs.current[`player_${foo}_all`].setAttrs({
				zIndex:players.length,
				preselected:false,
			});

			refs.current['layer'].draw();
		}, ((selection_animation_duration / 2) * 1000));

		if(display_player_text) {
			refs.current[`player_${preselected_player.current}_text_all`].to({
				scaleX:1,
				scaleY:1,
				y:item_sizes.current.player_size,
				opacity:1,
				duration:selection_animation_duration,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current[`player_${preselected_player.current}_selected_text`].to({
				opacity:0,
				duration:selection_animation_duration,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`player_${preselected_player.current}_all`].to({
			x:refs.current[`player_${preselected_player.current}_all`].attrs.initial_x,
			y:refs.current[`player_${preselected_player.current}_all`].attrs.initial_y,
			scaleX:1,
			scaleY:1,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				in_transition.current = false;
			},
		});

		in_transition.current = true;
		preselected_player.current = false;
	};

	const appearCTAs = () => {
		const card_props = refs.current[`player_${preselected_player.current}_all`].getClientRect();

		const card_bottom_edge = card_props.y + card_props.height;

		refs.current['confirm_actions'].to({
			y:card_bottom_edge - cta_size.current / 2 + 3 * padding.current - padding.current / 2 * cta_icon_scale,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['ok_cta'].to({
			y:0,
			duration:1.3 * selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const disappearCTAs = () => {
		refs.current['confirm_actions'].to({
			y:size.current.h + 2 * cta_size.current,
			duration:1.3 * selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				refs.current['ko_cta'].setAttrs({
					y:0,
				});

				refs.current['ok_cta'].setAttrs({
					y:cta_size.current * 2,
				});
			},
		});

		refs.current['ko_cta'].to({
			y:cta_size.current * 2,
			duration:selection_animation_duration,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const hoverPlayer = (player_num, direction = 1) => {
		if(is_playable.current === false) return;
		if(preselected_player.current !== false) return;

		if(direction > 0) {
			players.map((foo, p) => {
				refs.current[`player_${p}`].to({
					scaleX:(p == player_num) ? 1.1 : 1,
					scaleY:(p == player_num) ? 1.1 : 1,
					duration:0.2,
					easing:Konva.Easings.StrongEaseInOut,
				});
			});

			return;
		}

		refs.current[`player_${player_num}`].to({
			scaleX:1,
			scaleY:1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const confirmPlayer = () => {
		if(in_transition.current === true) return;
		if(is_playable.current === false) return;

		in_transition.current = true;

		animateStars();
		disappearCTAs();
		voteForPlayer(preselected_player.current);

		animateSelected(() => {
			finishGame();
		});
	};

	const mountBoard = () => {
		if(already_mounted.current) return;

		in_transition.current = true;
		preselected_player.current = false;
		if(is_builder && !is_test) is_playable.current = false;
		let completed = 0;
		players.map((foo, p) => {
			setTimeout(() => {
				if(refs.current[`player_${p}_all`] == undefined) return;

				if(display_player_text) {
					refs.current[`player_${p}_text_all`].to({
						opacity:1,
						duration:1 + 0.5 * Math.random() - 0.25,
						easing:Konva.Easings.StrongEaseInOut,
					});
				}

				refs.current[`player_${p}_all`].to({
					x:refs.current[`player_${p}_all`].attrs.initial_x,
					y:refs.current[`player_${p}_all`].attrs.initial_y,
					scaleX:1,
					scaleY:1,
					rotation:0,
					duration:1 + 0.5 * Math.random() - 0.25,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						completed++;

						if(completed == players.length) {
							enablePickPlayer();
							in_transition.current = false;

							already_mounted.current = true;
						}
					},
				});
			}, (p * 20));
		});
	};

	if(!full_loaded) return null;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const extra_x = (size.current.w - (num_players_per_row * item_sizes.current.player_size + (num_players_per_row + 1) * padding.current)) / 2;

	const bg_image_scale = (bg_image !== false) ? (size.current.w / Math.min((bg_image?.image_w ?? standard_board_size.w), (bg_image?.image_h ?? standard_board_size.h))) : 1;

	const is_touch_device = ((window?.platform ?? 'desktop') == 'mobile') ? true : false;
	const prevent_default = (is_touch_device) ? false : null;

	const text_default_props = {
		width:item_sizes.current.player_text_w,
		height:player_text_h.current,
		align:'center',
		fontStyle:'bold',
		verticalAlign:'middle',
		fontSize:22 * player_scale.current,
		preventDefault:prevent_default,
	};

	const cta_icon_scale = cta_size.current / (size.current.w / standard_cta_button_size);

	const y_delta = (size.current.h - contents_h.current) / 2;

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current.w}
			height={size.current.h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					fill={bg_color}
					width={size.current.w}
					height={size.current.h}
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
					preventDefault={prevent_default}
				/>

				{players.map((player, p) => {
					const col = p % num_players_per_row;
					const row = Math.floor(p / num_players_per_row);

					const pos_x = col * (item_sizes.current.player_size + padding.current) + padding.current + extra_x + item_sizes.current.player_size / 2;
					const pos_y = y_delta + row * (item_sizes.current.player_size + padding.current + player_text_h.current) + padding.current + (item_sizes.current.player_size + player_text_h.current) / 2;

					return (
						<Group
							key={`player_${p}_bg`}
							ref={(node) => { refs.current[`player_${p}_all`] = node; }}
							x={size.current.w / 2}
							y={-1.2 * (item_sizes.current.player_size + player_text_h.current) - pos_y}
							rotation={(!is_builder || is_test) ? (40 * Math.random() - 20) : 0}
							initial_x={pos_x}
							initial_y={pos_y}
							preselected={false}
							offsetX={item_sizes.current.player_size / 2}
							offsetY={(item_sizes.current.player_size + player_text_h.current) / 2}
							onMouseEnter={() => hoverPlayer(p, 1)}
							onMouseLeave={() => hoverPlayer(p, -1)}
							onMouseUp={() => selectPlayer(p)}	// onClick
							onTouchStart={(e) => {
								start_tap_y.current = e.evt.changedTouches[0].clientY;
							}}	// onTapStart
							onTouchEnd={(e) => {
								const final_tap_y = e.evt.changedTouches[0].clientY;
								if(Math.abs(final_tap_y - start_tap_y.current) < 50) selectPlayer(p);
								start_tap_y.current = false;
							}}	// onTapEnd
							preventDefault={prevent_default}
						>
							<Rect
								ref={(node) => { refs.current[`player_${p}_bg_cover`] = node; }}
								fill='white'
								opacity={0}
								width={item_sizes.current.player_size}
								height={item_sizes.current.player_size + player_text_h.current + 2 * padding.current}
								preventDefault={prevent_default}
							/>

							<Rect
								fill={getColor(p)}
								opacity={(display_background_color) ? 0.3 : 0}
								width={item_sizes.current.player_size}
								height={item_sizes.current.player_size + player_text_h.current}
								preventDefault={prevent_default}
							/>

							<Group
								ref={(node) => { refs.current[`player_${p}_text_all`] = node; }}
								x={(item_sizes.current.player_text_w + player_text_padding) / 2}
								y={item_sizes.current.player_size}
								offsetX={item_sizes.current.player_text_w / 2}
								preventDefault={prevent_default}
								opacity={(display_player_text) ? 1 : 0}
							>
								{(text_border_width > 0) && (
									<Text
										ref={(node) => { refs.current[`player_${p}_text_shadow`] = node; }}
										text={(isNotEmpty(player, 'name')) ? player.name : `Option ${(p + 1)}`}
										fill='white'
										strokeWidth={text_border_width * 2}
										lineCap='round'
										lineJoin='round'
										stroke='white'
										{...text_default_props}
									/>
								)}

								<Text
									ref={(node) => { refs.current[`player_${p}_text`] = node; }}
									text={(isNotEmpty(player, 'name')) ? player.name : `Option ${(p + 1)}`}
									fill={(text_color_overwrite) ? text_color : getColor(p)}
									{...text_default_props}
								/>
							</Group>

							<Group
								ref={(node) => { refs.current[`player_${p}_selected_text`] = node; }}
								x={(item_sizes.current.player_text_w + player_text_padding) / 2}
								y={item_sizes.current.player_size}
								offsetX={item_sizes.current.player_text_w / 2}
								opacity={0}
								preventDefault={prevent_default}
							>
								<Text
									text={(isNotEmpty(player, 'name')) ? player.name : `Option ${(p + 1)}`}
									fill='rgba(0, 0, 0, 1)'
									{...text_default_props}
								/>
							</Group>

							<CardPlayerImage
								index={p}
								player={player}
								stage={stage}
								refs={refs.current}
								image_data={indexed_images[`image_${player.id}`]}
								item_sizes={item_sizes.current}
								padding={padding.current}
								canvas_scale={canvas_scale.current}
								prevent_default={prevent_default}
								color={getColor(p)}
							/>
						</Group>
					)
				})}

				<Rect
					ref={(node) => { refs.current['tapper'] = node; }}
					fill='rgba(0, 0, 0, 1)'
					width={size.current.w}
					height={size.current.h}
					x={size.current.w + 10}
					opacity={0.7}
					onMouseUp={undoSelection}	// onClick
					onTouchStart={(e) => {
						start_tap_y.current = e.evt.changedTouches[0].clientY;
					}}	// onTapStart
					onTouchEnd={(e) => {
						const final_tap_y = e.evt.changedTouches[0].clientY;
						if(Math.abs(final_tap_y - start_tap_y.current) < 50) undoSelection();
						start_tap_y.current = false;
					}}	// onTapEnd
					preventDefault={prevent_default}
				/>

				<Group
					ref={(node) => { refs.current['final_gradient'] = node; }}
					opacity={0}
				/>

				<Group
					ref={(node) => { refs.current['confirm_actions'] = node; }}
					x={size.current.w / 2}
					y={size.current.h + 3 * cta_size.current}
					scaleX={0.6}
					scaleY={0.6}
				>
					<Group
						ref={(node) => { refs.current['ko_cta'] = node; }}
						x={-size.current.w / 6}
						onMouseUp={undoSelection}		// onClick
						onTouchEnd={undoSelection}	// onTap
					>
						<Circle
							radius={cta_size.current + padding.current / 2 * cta_icon_scale}
							fill='rgba(0, 0, 0, 1)'
							opacity={0.2}
						/>

						<Circle
							radius={cta_size.current}
							fill='rgba(215, 35, 60, 1)'
							strokeWidth={padding.current / 2 * cta_icon_scale}
							stroke={cta_stroke_color}
						/>

						<Line
							points={[-27.93, 27.93, 27.93, -27.93]}
							strokeWidth={1.5 * padding.current}
							stroke={cta_stroke_color}
							scaleX={cta_icon_scale}
							scaleY={cta_icon_scale}
						/>

						<Line
							points={[-27.93, -27.93, 27.93, 27.93]}
							strokeWidth={1.5 * padding.current}
							stroke={cta_stroke_color}
							scaleX={cta_icon_scale}
							scaleY={cta_icon_scale}
						/>
					</Group>

					<Group
						ref={(node) => { refs.current['ok_cta'] = node; }}
						x={size.current.w / 6}
						y={cta_size.current * 2}
						onMouseUp={confirmPlayer}		// onClick
						onTouchEnd={confirmPlayer}	// onTap
					>
						<Circle
							radius={cta_size.current + padding.current / 2 * cta_icon_scale}
							fill='rgba(0, 0, 0, 1)'
							opacity={0.2}
						/>

						<Circle
							radius={cta_size.current}
							fill='rgba(120, 195, 0, 1)'
							strokeWidth={padding.current / 2 * cta_icon_scale}
							stroke={cta_stroke_color}
						/>

						<Path
							data="M-33.91,-2.64 -9.54,21.72 33.91,-21.72"
							strokeWidth={1.5 * padding.current}
							stroke={cta_stroke_color}
							scaleX={cta_icon_scale}
							scaleY={cta_icon_scale}
						/>
					</Group>
				</Group>

				{/* LITTLE STARS - ini */}
				<Group
					ref={(node) => { refs.current['all_stars'] = node; }}
					offsetX={whole_stars_size / 2}
					offsetY={whole_stars_size / 2}
					scaleX={100 / whole_stars_size}
					scaleY={100 / whole_stars_size}
					x={size.current.w / 2 + whole_stars_size / 2 - 2 * padding.current}
					y={size.current.h / 3 - whole_stars_size / 2 * 100 / whole_stars_size}
				>
					{stars.map((star, s) => {
						const star_scale = star.size / default_star_size;

						return (
							<Group
								key={`star_${s + 1}`}
								ref={(node) => { refs.current[`star_${s + 1}`] = node; }}
								scaleX={0}
								scaleY={0}
								offsetX={default_star_size / 2}
								offsetY={default_star_size / 2}
								x={star.x}
								y={star.y}
							>
								<Path
									data='M25.86,54.14C33.82,62.11,32.63,80,40,80s6.18-17.89,14.14-25.86S80,47.37,80,40s-17.89-6.18-25.86-14.14S47.37,0,40,0s-6.18,17.89-14.14,25.86S0,32.63,0,40S17.89,46.18,25.86,54.14z'
									fill='white'
									stroke='black'
									strokeWidth={1.5 / star_scale}
								/>
							</Group>
						)
					})}
				</Group>
				{/* LITTLE STARS - end */}
			</Layer>
		</Stage>
	)
})

export default PickFavoriteCanvas;